import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card } from "reactstrap";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";

import {
  fetchStateAndYearDropdowns,
  paymentSlabStatusOptions,
  createConcessionSlab,
} from "./helper";

import { updateRequest } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { PermisionDenied } from "views/pages/manage/common/commonComponents";
import { useGetPermissions } from "layouts/customHook";

const CreateNewCommissionSlab = () => {
  const history = useHistory();
  const userPermissions = useGetPermissions('commissionSlab');

  const masterDropdowns = useSelector(
    (state) => state?.globalData?.masterDropdowns
  );


  const [dropdownOptions, setDropdownOptions] = useState({
    yearOptions: [],
  });

  const [form, setForm] = useState({
    businessUnit: null,
    extensionBusinessUnit: null,
    year: null,
    status: null,
    sapPlantId: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);


  const buOptions = useMemo(() => {
    return (masterDropdowns?.businessArea || []).map((item) => ({
      label: item.label,
      value: item.label,
      ...item
    }));
  }, [masterDropdowns]);

  const buFranchiseSubTypeFECList = useMemo(() => {
    return (masterDropdowns?.businessArea || []).filter((item) => {
      return item.franchiseSubType == "F+EC"
    }).map((item) => ({
      label: item.label,
      value: item.label,
      ...item
    }));
  }, [masterDropdowns]);

  const isFormValid = useMemo(() => {
    return (
      form.businessUnit &&
      form.extensionBusinessUnit &&
      form.year &&
      form.status &&
      form.sapPlantId?.trim()
    );
  }, [form]);


  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const options = await fetchStateAndYearDropdowns();
      const currentYear = parseInt(moment().format("YYYY"), 10);

      const filteredYearOptions = (options?.yearOptions || []).filter(
        (opt) => {
          const yearVal = parseInt(opt.label, 10);
          return !isNaN(yearVal) && yearVal >= currentYear;
        }
      );

      setDropdownOptions({
        ...options,
        yearOptions: filteredYearOptions,
      });
    } catch (err) {
      console.error("Failed to load dropdowns:", err);
    }
  };


  const handleFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSaveChanges = async () => {
    if (!isFormValid || saving) return;

    setSaving(true);
    setError(null);

    try {
      const payload = updateRequest({
        ...form,
        sapPlantId: form.sapPlantId.trim(),
      });

      const response = await createConcessionSlab(payload);

      if (response?.id) {
        history.push(`/admin/commissionSlab/${response.id}`);
      }
    } catch (err) {
      console.error("Error creating slab:", err);
      setError("Unable to create slab. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    (userPermissions||[]).includes("C") ? (
      <div className="p-4">
        {/* Header */}
        <div className="d-flex align-items-center mb-3">
          <BackIcon
            style={{ cursor: "pointer" }}
            onClick={() => history.goBack()}
          />
          <h3 className="heading-3 mb-0 ml-2">Add New Commission Slab</h3>
        </div>

        <Card className="p-4 mb-4">
          <h3 className="mb-4 text-bold">Slab Setup</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <AAutoComplete
              label="F + EC Business Unit *"
              placeholder="Select"
              items={buFranchiseSubTypeFECList}
              currentValue={form.businessUnit}
              handleChange={(value) => {
                handleFormChange("businessUnit", value);
                handleFormChange("sapPlantId", value?.sapPlantId || "");
              }}
              height="45px"
              isMulti={false}
            />

            <AAutoComplete
              label="Extension Business Unit *"
              placeholder="Select"
              items={buOptions}
              currentValue={form.extensionBusinessUnit}
              handleChange={(value) =>
                handleFormChange("extensionBusinessUnit", value)
              }
              height="45px"
              isMulti={false}
            />

            <AAutoComplete
              label="Year *"
              placeholder="Select"
              items={dropdownOptions?.yearOptions || []}
              currentValue={form.year}
              handleChange={(value) => handleFormChange("year", value)}
              height="45px"
              isMulti={false}
            />

            <AAutoComplete
              label="Status *"
              placeholder="Select"
              items={paymentSlabStatusOptions}
              currentValue={form.status}
              handleChange={(value) => handleFormChange("status", value)}
              height="45px"
              isMulti={false}
            />
            <div>
              <AInput
                label="SAP Plant ID *"
                value={form.sapPlantId}
                handler={(value) =>
                  handleFormChange("sapPlantId", value)
                }
                disabled={true}
                style={{ height: "45px" }}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-danger mb-3">
              {error}
            </div>
          )}

          {/* Save Button */}
          <div style={{ width: "220px" }}>
            <AButton
              variant="primary_filled"
              onClick={handleSaveChanges}
              disabled={!isFormValid || saving}
              className="w-100"
            >
              {saving ? "Saving..." : "Save Changes"}
            </AButton>
          </div>
        </Card>
      </div>
    ) : (
      <PermisionDenied />
    )
  )
};

export default CreateNewCommissionSlab;
